import React from "react";
import { FiCheck, FiPlay } from "react-icons/fi";
import type { RecoveryLessonStatus as LessonStatus } from "../../types/recovery";
import { createLogger } from "../../lib/logger";

const log = createLogger("LessonCard");

interface LessonCardProps {
  title: string;
  duration: string;
  hasVideo: boolean;
  status: LessonStatus;
  percentage?: number;
  onClick: () => void;
  isRecommended?: boolean;
  recommendationText?: string; // Dynamic label text
}

export const LessonCard: React.FC<LessonCardProps> = React.memo(({
  title,
  duration,
  hasVideo,
  status,
  percentage = 0,
  onClick,
  isRecommended = false,
  recommendationText = "Recommended",
}) => {
  const isCompleted = status === "completed";
  const isInProgress = status === "in_progress";
  const isDisabled = !hasVideo;

  const size = 44; 
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <button
      type="button"
      onClick={() => { 
        log.info("Lesson tapped", { title, status, hasVideo }); 
        onClick(); 
      }}
      disabled={isDisabled}
      className={`w-full flex items-center gap-4 p-3 rounded-2xl border transition-all duration-200 cursor-pointer 
        ${isRecommended ? 'border-brand-primary/30 bg-brand-light/5 shadow-sm' : 'border-gray-100 bg-white'} 
        hover:bg-gray-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {/* 1. Left Section: Progress Circle */}
      <div className="relative flex-shrink-0 flex items-center justify-center" style={{ width: size, height: size }}>
        {isCompleted ? (
          <div className="w-full h-full rounded-full bg-green-500 flex items-center justify-center shadow-sm">
            <FiCheck size={20} className="text-white" strokeWidth={4} />
          </div>
        ) : isInProgress ? (
          <div className="relative flex items-center justify-center">
            <svg width={size} height={size} className="rotate-[-90deg]">
              <circle cx={size / 2} cy={size / 2} r={radius} stroke="#F3F4F6" strokeWidth={strokeWidth} fill="transparent" />
              <circle
                cx={size / 2} cy={size / 2} r={radius} stroke="#1E5E5B" strokeWidth={strokeWidth} fill="transparent"
                strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                className="transition-all duration-700 ease-out"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-brand-dark font-dm-sans">
              {percentage}%
            </span>
          </div>
        ) : (
          <div className="w-full h-full rounded-full bg-brand-light flex items-center justify-center text-brand-primary group">
            <div className="w-full h-full rounded-full border-2 border-brand-primary/10 flex items-center justify-center">
              <FiPlay size={16} fill="currentColor" className="ml-0.5 transition-transform group-hover:scale-110" />
            </div>
          </div>
        )}
      </div>

      {/* 2. Middle Section: Title & Duration */}
      <div className="flex-1 text-left min-w-0">
        <h4 className={`m-0 text-[16px] font-bold font-dm-sans leading-tight truncate transition-colors ${isCompleted ? 'text-gray-400' : 'text-brand-dark'}`}>
          {title}
        </h4>
        <p className="m-0 mt-0.5 text-[11px] font-medium text-gray-400 font-dm-sans uppercase tracking-tight">
          {duration}
        </p>
      </div>

      {/* 3. Right Section: Dynamic Guidance Label */}
      {isRecommended && !isCompleted && (
        <div className="flex-shrink-0 flex flex-col items-end gap-1 px-1">
          <span className="text-[8px] font-bold text-brand-primary uppercase tracking-widest font-dm-sans leading-none">
            {recommendationText.includes("Completion") ? "Milestone" : "Up Next"}
          </span>
          <div className="flex items-center gap-1 bg-brand-light px-2 py-1 rounded-full border border-brand-primary/10 shadow-sm">
            <span className="text-[9px] font-bold text-brand-primary font-dm-sans whitespace-nowrap">
              {recommendationText}
            </span>
          </div>
        </div>
      )}
    </button>
  );
});

LessonCard.displayName = "LessonCard";
export default LessonCard;